import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useSocket } from "./SocketContext";

interface VersionInfo {
  version: string;
  buildNumber: number;
  buildTime: string;
}

interface VersionContextType {
  currentVersion: VersionInfo | null;
  latestVersion: VersionInfo | null;
  isUpdateAvailable: boolean;
  isChecking: boolean;
  checkForUpdates: () => Promise<void>;
  dismissUpdate: () => void;
  reload: () => void;
}

const VersionContext = createContext<VersionContextType | null>(null);

// Check for updates every 5 minutes
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000;

export function VersionProvider({ children }: { children: ReactNode }) {
  const { socket } = useSocket();
  
  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Load current version on mount
  useEffect(() => {
    const loadCurrentVersion = async () => {
      try {
        // Add cache-busting to ensure we get the current deployed version
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();
          setCurrentVersion(data);
          setLatestVersion(data);
        }
      } catch (error) {
        console.error("Failed to load current version:", error);
      }
    };

    loadCurrentVersion();
  }, []);

  // Check for updates
  const checkForUpdates = useCallback(async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    try {
      // Force fresh fetch with cache busting
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      
      if (response.ok) {
        const data: VersionInfo = await response.json();
        setLatestVersion(data);
        
        // Compare build numbers or version hashes
        if (currentVersion && data.buildNumber !== currentVersion.buildNumber) {
          setIsUpdateAvailable(true);
        }
      }
    } catch (error) {
      console.error("Failed to check for updates:", error);
    } finally {
      setIsChecking(false);
    }
  }, [currentVersion, isChecking]);

  // Periodic update check
  useEffect(() => {
    const intervalId = setInterval(checkForUpdates, UPDATE_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [checkForUpdates]);

  // Listen for version_update socket event
  useEffect(() => {
    if (!socket) return;

    const handleVersionUpdate = (data: VersionInfo) => {
      console.log("[Version] Received version_update:", data);
      setLatestVersion(data);
      
      if (currentVersion && data.buildNumber !== currentVersion.buildNumber) {
        setIsUpdateAvailable(true);
      }
    };

    socket.on("version_update", handleVersionUpdate);

    return () => {
      socket.off("version_update", handleVersionUpdate);
    };
  }, [socket, currentVersion]);

  const dismissUpdate = useCallback(() => {
    setIsDismissed(true);
    setIsUpdateAvailable(false);
  }, []);

  const reload = useCallback(() => {
    // Clear service worker cache if exists
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "SKIP_WAITING" });
    }
    // Force reload from server
    window.location.reload();
  }, []);

  // Reset dismissed state when a new version is detected
  useEffect(() => {
    if (latestVersion && currentVersion) {
      if (latestVersion.buildNumber !== currentVersion.buildNumber && !isDismissed) {
        setIsUpdateAvailable(true);
      }
    }
  }, [latestVersion, currentVersion, isDismissed]);

  return (
    <VersionContext.Provider
      value={{
        currentVersion,
        latestVersion,
        isUpdateAvailable: isUpdateAvailable && !isDismissed,
        isChecking,
        checkForUpdates,
        dismissUpdate,
        reload,
      }}
    >
      {children}
    </VersionContext.Provider>
  );
}

export function useVersion() {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error("useVersion must be used within a VersionProvider");
  }
  return context;
}
